import React, { useCallback, useEffect, useState } from 'react';
import { ConditionReport, ConditionReportPhoto, ConditionReportRole, getErrorMessage } from '../types';
import api from '../api';
import { useTranslation, Lang } from '../i18n';
import { formatDateTime } from '../formatDate';
import { StarRating } from './StarRating';
import { HandoverWaitingBlock } from './HandoverWaitingBlock';
import { FormLabel } from './FormLabel';
import { FieldError } from './FieldError';
import { ConditionReportPhotos } from './ConditionReportPhotos';

interface Props {
  orderId: number;
  role: ConditionReportRole;
  /** Can the viewer create / edit / supersede reports (rental still active). */
  editable: boolean;
  /** Can the viewer confirm the counterparty's report (allowed even after completion). */
  canConfirm: boolean;
  reports: ConditionReport[];
  /** Rating the lender received (given by the renter). */
  lenderRating?: number;
  /** Rating the renter received (given by the lender). */
  renterRating?: number;
  lang: Lang;
  onChanged: () => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

const SIDES = [
  { side: 0, labelKey: 'reports.pickup', descKey: 'reports.pickup_desc' },
  { side: 1, labelKey: 'reports.return', descKey: 'reports.return_desc' },
] as const;

export const ConditionReportPanel: React.FC<Props> = ({
  orderId, role, editable, canConfirm, reports, lenderRating, renterRating, lang, onChanged, onError, onSuccess,
}) => {
  const { t } = useTranslation();

  // The rating the viewer has already given to the counterparty.
  const myGivenRating = role === 'lender' ? renterRating : lenderRating;

  const [photos, setPhotos] = useState<ConditionReportPhoto[]>([]);
  const loadPhotos = useCallback(async () => {
    try {
      const res = await api.get<ConditionReportPhoto[]>(`/orders/${orderId}/reports/photos`);
      setPhotos(res.data);
    } catch {
      // Photos are supplementary to the report view; a failed load leaves the list as it was.
    }
  }, [orderId]);

  // Reports change when either party acts (including uploads by the counterparty), so reload with them.
  useEffect(() => { void loadPhotos(); }, [loadPhotos, reports]);

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <h3 style={{ fontSize: '1rem', marginBottom: '0.25rem' }}>{t('reports.title')}</h3>
      <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
        {t('reports.help')}
      </p>
      {SIDES.map(({ side, labelKey, descKey }) => (
        <ReportSideCard
          key={side}
          orderId={orderId}
          side={side}
          label={t(labelKey)}
          description={t(descKey)}
          role={role}
          editable={editable}
          canConfirm={canConfirm}
          reports={reports.filter(r => r.side === side)}
          photos={photos.filter(p => p.side === side)}
          onPhotosChanged={loadPhotos}
          myGivenRating={myGivenRating}
          lang={lang}
          onChanged={onChanged}
          onError={onError}
          onSuccess={onSuccess}
        />
      ))}
    </div>
  );
};

interface SideCardProps {
  orderId: number;
  side: number;
  label: string;
  description: string;
  role: ConditionReportRole;
  editable: boolean;
  canConfirm: boolean;
  reports: ConditionReport[];
  photos: ConditionReportPhoto[];
  onPhotosChanged: () => void;
  myGivenRating?: number;
  lang: Lang;
  onChanged: () => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

const ReportSideCard: React.FC<SideCardProps> = ({
  orderId, side, label, description, role, editable, canConfirm, reports, photos, onPhotosChanged, myGivenRating, lang, onChanged, onError, onSuccess,
}) => {
  const { t } = useTranslation();
  const [formOpen, setFormOpen] = useState(false);
  const [content, setContent] = useState('');
  const [damageReported, setDamageReported] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Mandatory rating of the counterparty for the return handover, prefilled to five stars.
  const [rating, setRating] = useState<number>(myGivenRating ?? 5);
  const [ratingError, setRatingError] = useState('');

  const current = reports.find(r => !r.superseded) ?? null;
  const history = reports.filter(r => r.superseded);
  const isReturn = side === 1;
  // Either party can record damage, but only in the return report.
  const canReportDamage = isReturn;
  const ratingLabel = role === 'lender' ? t('reports.rate_customer') : t('reports.rate_owner');

  const openForm = (prefill: string, prefillDamage = false) => {
    setContent(prefill);
    setDamageReported(prefillDamage);
    setRating(myGivenRating ?? 5);
    setRatingError('');
    setFormOpen(true);
  };

  const validateRating = () => {
    if (isReturn && (!rating || rating < 1)) {
      setRatingError(t('forms.rating_required'));
      return false;
    }
    setRatingError('');
    return true;
  };

  const submit = async () => {
    if (isReturn && !validateRating()) return;
    if (damageReported && !content.trim()) {
      onError(t('reports.damage_description_required'));
      return;
    }
    setSubmitting(true);
    onError('');
    try {
      await api.post(`/orders/${orderId}/reports`, {
        side,
        content: content.trim() || null,
        rating: isReturn ? rating : undefined,
        damageReported: canReportDamage && damageReported,
      });
      setFormOpen(false);
      setContent('');
      onSuccess(t('reports.submit_success'));
      onChanged();
    } catch (err) {
      onError(getErrorMessage(err, t('reports.submit_error')));
    } finally {
      setSubmitting(false);
    }
  };

  const confirm = async () => {
    if (!current) return;
    if (isReturn && !validateRating()) return;
    setConfirming(true);
    onError('');
    try {
      await api.post(`/orders/${orderId}/reports/${current.id}/confirm`, isReturn ? { rating } : {});
      onSuccess(t('reports.confirm_success'));
      onChanged();
    } catch (err) {
      onError(getErrorMessage(err, t('reports.confirm_error')));
    } finally {
      setConfirming(false);
    }
  };

  const roleLabel = (r: ConditionReportRole) => (r === role ? t('reports.you') : t('reports.other_party'));

  const ratingPicker = (
    <div className={`form-group${ratingError ? ' form-group--error' : ''}`} style={{ marginTop: '0.5rem', marginBottom: 0 }}>
      <FormLabel required>{ratingLabel}</FormLabel>
      <StarRating value={rating} onChange={v => { setRating(v); setRatingError(''); }} />
      <FieldError message={ratingError} />
    </div>
  );

  return (
    <div style={{ borderTop: '1px solid var(--color-border, #eee)', paddingTop: '0.75rem', marginTop: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem' }}>
        <strong style={{ fontSize: '0.95rem' }}>{label}</strong>
      </div>
      <p className="text-muted" style={{ fontSize: '0.8rem', margin: '0.15rem 0 0.5rem' }}>{description}</p>

      <ConditionReportPhotos
        orderId={orderId}
        side={side}
        role={role}
        photos={photos}
        canUpload={editable}
        lang={lang}
        onUploaded={onPhotosChanged}
        onError={onError}
      />

      {current && (
        <div style={{ background: 'var(--color-surface, #fafafa)', border: '1px solid var(--color-border, #eee)', borderRadius: '6px', padding: '0.6rem 0.75rem' }}>
          <p className="text-muted" style={{ fontSize: '0.78rem', marginBottom: '0.25rem' }}>
            {t('reports.drafted_by', { who: roleLabel(current.draftedByRole) })} · {formatDateTime(current.draftedAt, lang)}
          </p>
          {current.damageReported && (
            <p style={{ fontSize: '0.82rem', color: '#c0392b', fontWeight: 600, margin: '0 0 0.25rem' }}>
              {t('reports.damage_badge')}
            </p>
          )}
          <p style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', margin: 0 }}>
            {current.content || <span className="text-muted">{t('reports.empty_content')}</span>}
          </p>
          {current.confirmedByRole ? (
            <p style={{ fontSize: '0.82rem', color: '#27ae60', fontWeight: 600, marginTop: '0.4rem', marginBottom: 0 }}>
              {t('reports.confirmed_by', { who: roleLabel(current.confirmedByRole) })}
            </p>
          ) : current.draftedByRole === role ? (
            <HandoverWaitingBlock orderId={orderId} />
          ) : (
            canConfirm && (
              <>
                {isReturn && ratingPicker}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" onClick={confirm} disabled={confirming}>
                    {confirming ? t('reports.confirm_submitting') : t('reports.confirm')}
                  </button>
                  {editable && (
                    <button className="btn btn-secondary btn-sm" onClick={() => openForm(current.content || '', current.damageReported)} disabled={confirming}>
                      {t('reports.propose_changes')}
                    </button>
                  )}
                </div>
              </>
            )
          )}
          {isReturn && myGivenRating != null && !formOpen && (
            <div style={{ marginTop: '0.5rem' }}>
              <span className="order-meta" style={{ marginRight: 6 }}>{t('reports.your_rating')}</span>
              <StarRating value={myGivenRating} size="sm" />
            </div>
          )}
        </div>
      )}

      {!current && !formOpen && (
        editable
          ? <button className="btn btn-secondary btn-sm" onClick={() => openForm('')}>{t('reports.start')}</button>
          : <p className="text-muted" style={{ fontSize: '0.85rem' }}>{t('reports.none')}</p>
      )}

      {current && !current.confirmedByRole && current.draftedByRole === role && editable && !formOpen && (
        <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.4rem' }} onClick={() => openForm(current.content || '', current.damageReported)}>
          {t('reports.edit')}
        </button>
      )}

      {formOpen && (
        <div style={{ marginTop: '0.5rem' }}>
          <textarea
            className="form-input"
            rows={3}
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder={t('reports.content_placeholder')}
            maxLength={2000}
          />
          {canReportDamage && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer', margin: '0.4rem 0' }}>
              <input
                type="checkbox"
                checked={damageReported}
                onChange={e => setDamageReported(e.target.checked)}
                disabled={submitting}
                style={{ marginTop: '0.2rem' }}
              />
              <span>{t('reports.damage_checkbox')}</span>
            </label>
          )}
          <p className="text-muted" style={{ fontSize: '0.78rem', margin: '0.4rem 0' }}>
            {t(isReturn ? 'reports.return_notice' : 'reports.pickup_notice')}
          </p>
          {isReturn && role === 'lender' && (
            <p style={{ fontSize: '0.78rem', color: '#856404', margin: '0.4rem 0' }}>
              {t('reports.return_completes_notice')}
            </p>
          )}
          {isReturn && ratingPicker}
          {photos.length === 0 && (
            <p style={{ fontSize: '0.78rem', color: '#856404', margin: '0.4rem 0' }}>{t('reports.photos_required')}</p>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            <button className="btn btn-primary btn-sm" onClick={submit} disabled={submitting || photos.length === 0}>
              {submitting ? t('reports.submitting') : t('reports.submit')}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setFormOpen(false); setContent(''); }} disabled={submitting}>
              {t('reports.cancel')}
            </button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setShowHistory(v => !v)}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-link, #1565c0)', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}
          >
            {showHistory ? t('reports.hide_previous') : t('reports.previous_versions', { count: history.length })}
          </button>
          {showHistory && history.map(h => (
            <div key={h.id} style={{ borderLeft: '2px solid var(--color-border, #ddd)', paddingLeft: '0.6rem', margin: '0.4rem 0', opacity: 0.7 }}>
              <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: '0.15rem' }}>
                {t('reports.drafted_by', { who: roleLabel(h.draftedByRole) })} · {formatDateTime(h.draftedAt, lang)}
              </p>
              <p style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', margin: 0 }}>
                {h.content || <span className="text-muted">{t('reports.empty_content')}</span>}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
